
import React from 'react'
import { Shield, AlertTriangle, CheckCircle } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

interface DocumentAnalysisResultProps {
  result: {
    risk_level: 'low' | 'medium' | 'high'
    sensitive_data_found: Record<string, number>
    analyzed_at: string
  }
}

export function DocumentAnalysisResult({ result }: DocumentAnalysisResultProps) {
  const getRiskLevelColor = (level: string) => {
    switch (level) {
      case 'low': return 'bg-green-100 text-green-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      case 'high': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getRiskIcon = (level: string) => {
    switch (level) {
      case 'low': return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'medium': return <AlertTriangle className="h-5 w-5 text-yellow-600" />
      case 'high': return <Shield className="h-5 w-5 text-red-600" />
      default: return null
    }
  }

  return (
    <Card className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Security Analysis</h3>
        <Badge className={getRiskLevelColor(result.risk_level)}>
          {getRiskIcon(result.risk_level)}
          <span className="ml-1 capitalize">{result.risk_level} Risk</span>
        </Badge>
      </div>

      {Object.keys(result.sensitive_data_found).length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium">Sensitive Data Found:</h4>
          <ul className="list-disc list-inside space-y-1">
            {Object.entries(result.sensitive_data_found).map(([pattern, count]) => (
              <li key={pattern} className="text-sm text-gray-600">
                {pattern}: {count} occurrences
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-gray-500">
        Analyzed at: {new Date(result.analyzed_at).toLocaleString()}
      </div>
    </Card>
  )
}
